/**
 * passport/achievements.ts — declarative, extensible achievement registry.
 *
 * Each achievement is a pure predicate over the passport profile — the module
 * evaluates the registry after every update and unlocks anything newly
 * satisfied (once, permanently). Future achievements register at runtime via
 * registerAchievementRule() — the registry supports unlimited entries.
 */

import type { PassportProfile } from './models';

export interface AchievementRule {
  id: string;
  title: string;
  description: string;
  xpAwarded: number;
  /** Pure predicate — no side effects, no external reads. */
  isSatisfied: (profile: PassportProfile) => boolean;
}

function stampCount(profile: PassportProfile, category: string): number {
  return profile.stamps.filter((s) => s.category === category).length;
}

const BUILTIN_RULES: AchievementRule[] = [
  {
    id: 'first-mission', title: 'First Mission', xpAwarded: 50,
    description: 'Completed your first WorkRoamer mission.',
    isSatisfied: (p) => p.missionCompletionCount >= 1,
  },
  {
    id: 'first-country', title: 'First Country', xpAwarded: 50,
    // Build 2.32.0: requires an active mission — location detection at boot is
    // NOT an accomplishment. XP only awards when the operator has genuinely
    // worked from a foreign country.
    description: 'Worked your first mission in a recorded country.',
    isSatisfied: (p) => p.countriesVisited.length >= 1 && p.missionCompletionCount >= 1,
  },
  {
    id: 'five-countries', title: 'Five Countries', xpAwarded: 150,
    description: 'Roamed across five countries.',
    isSatisfied: (p) => p.countriesVisited.length >= 5,
  },
  {
    id: 'ten-cities', title: 'Ten Cities', xpAwarded: 150,
    description: 'Based yourself in ten different cities.',
    isSatisfied: (p) => p.citiesVisited.length >= 10,
  },
  {
    id: 'hundred-missions', title: '100 Missions', xpAwarded: 500,
    description: 'Completed one hundred missions.',
    isSatisfied: (p) => p.missionCompletionCount >= 100,
  },
  {
    id: 'thousand-xp', title: '1000 XP', xpAwarded: 100,
    description: 'Earned a thousand experience points.',
    isSatisfied: (p) => p.totalXp >= 1000,
  },
  {
    id: 'coffee-explorer', title: 'Coffee Explorer', xpAwarded: 75,
    description: 'Collected five café or coworking stamps.',
    isSatisfied: (p) => stampCount(p, 'coworking') >= 5,
  },
  {
    id: 'coworking-champion', title: 'Coworking Champion', xpAwarded: 150,
    description: 'Collected ten coworking stamps.',
    isSatisfied: (p) => stampCount(p, 'coworking') >= 10,
  },
  {
    id: 'community-builder', title: 'Community Builder', xpAwarded: 100,
    description: 'Joined five community meetups.',
    isSatisfied: (p) => stampCount(p, 'communityMeetup') >= 5,
  },
  {
    id: 'weekend-explorer', title: 'Weekend Explorer', xpAwarded: 75,
    description: 'Collected five special event stamps.',
    isSatisfied: (p) => stampCount(p, 'specialEvent') >= 5,
  },
  {
    id: 'night-market-hunter', title: 'Night Market Hunter', xpAwarded: 75,
    description: 'Collected ten special event or seasonal stamps.',
    isSatisfied: (p) => stampCount(p, 'specialEvent') + stampCount(p, 'seasonal') >= 10,
  },
  {
    id: 'remote-pro', title: 'Remote Pro', xpAwarded: 250,
    description: 'Fifty missions completed across three or more countries.',
    isSatisfied: (p) => p.missionCompletionCount >= 50 && p.countriesVisited.length >= 3,
  },
  {
    id: 'global-citizen', title: 'Global Citizen', xpAwarded: 500,
    description: 'Ten countries and twenty cities on record.',
    isSatisfied: (p) => p.countriesVisited.length >= 10 && p.citiesVisited.length >= 20,
  },
];

const registry = new Map<string, AchievementRule>(
  BUILTIN_RULES.map((r) => [r.id, r]),
);

export function getAchievementRules(): AchievementRule[] {
  return Array.from(registry.values());
}

/** Register a future achievement at runtime. Existing ids are not replaced. */
export function registerAchievementRule(rule: AchievementRule): boolean {
  if (registry.has(rule.id)) return false;
  registry.set(rule.id, rule);
  return true;
}
